import React, { useEffect, useState, useMemo } from "react";
import { Box } from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import { ByzMuiDateTimePicker } from "src/components/core-components/mui/inputs/ByzMuiDateTimePicker";
import ByzMuiTextField from "src/components/core-components/mui/inputs/ByzMuiTextField";
import { ByzMuiCheckBox } from "src/components/core-components/mui/ByzMuiCheckBox";
import { calculateBMI } from "./helpers/bmiCalculator";
import ByzMuiTypography from "src/components/core-components/mui/inputs/ByzMuiTypography";
import { useTranslate } from "src/locales";
import { useAppSelector } from "src/app/hooks";
import { useParams } from "react-router-dom";
import ByzNurseSaveButton from "src/components/core-components/mui/buttons/ByzNurseButton/ByzNurseSaveButton";
import ByzMuiSnackBar from "src/components/core-components/mui/ByzMuiSnackBar";
import { referenceService } from "src/api/references/referenceService";
import dayjs from "dayjs";
import { toLocalISOWithMillis } from "src/utils/date/dateHelper";
import { usePatientUpn } from "../../hooks/usePatientUpn";

type HeightWeightCreateProps = {
  mode?: "create" | "edit";
  initialData?: Partial<{
    id: number;
    height?: number | string | null;
    weight?: number | string | null;
    vki?: number | string | null;
    description?: string | null;
    write_on_epicrise?: string | null;
    vki_mcpd_id?: number | null;
    ptime?: string | null;
  }>;
  onSaved?: () => void;
  onCloseModal?: () => void;
  recordId?: number;
  onShowMessage?: (params: { message: string; type: "success" | "error" }) => void;
};

function HeightWeightCreate({
  mode = "create",
  initialData,
  onSaved,
  onCloseModal,
  recordId,
  onShowMessage,
}: HeightWeightCreateProps) { 
  const { t } = useTranslate();
  const { id: routePatientId } = useParams();
  const mainTabs = useAppSelector((state) => state.tabs.mainTabs);
  const activeTabIndex = useAppSelector((state) => state.tabs.activeTabIndex);
  const selectedPatientId = mainTabs?.[activeTabIndex]?.data?.patientId;
  const authUser = useAppSelector((state) => state.auth.user);
  const patientIdNumber = Number(routePatientId || selectedPatientId || 0);

  const displayUser = useMemo(() => {
    return authUser?.username || "";
  }, [authUser]);

  const [startDate, setStartDate] = useState(new Date());
  const [weight, setWeight] = useState("");
  const [height, setHeight] = useState("");
  const [vki, setVki] = useState("");
  const [obesityStatus, setObesityStatus] = useState("");
  const [description, setDescription] = useState("");
  const [writeOnEpicrise, setWriteOnEpicrise] = useState(false);
  const [saving, setSaving] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const initialUpn = mainTabs?.[activeTabIndex]?.data?.upn ?? null;
  const { upn, setUpn, isFetchingUpn } = usePatientUpn(patientIdNumber, initialUpn);

  const showMessage = React.useCallback(
    (message: string, type: "success" | "error") => {
      if (onShowMessage) {
        onShowMessage({ message, type });
        return;
      }
      setAlertMessage(message);
      setAlertType(type);
      setAlertOpen(true);
    },
    [onShowMessage]
  );

  // initial data (edit)
  useEffect(() => {
    if (!initialData) return;
    if (initialData.height !== undefined && initialData.height !== null) {
      setHeight(String(initialData.height));
    }
    if (initialData.weight !== undefined && initialData.weight !== null) {
      setWeight(String(initialData.weight));
    }
    if (initialData.vki !== undefined && initialData.vki !== null) {
      setVki(String(initialData.vki));
    }
    if (initialData.description !== undefined && initialData.description !== null) {
      setDescription(initialData.description as string);
    }
    if (initialData.write_on_epicrise !== undefined && initialData.write_on_epicrise !== null) {
      setWriteOnEpicrise(initialData.write_on_epicrise === "1");
    }
    if (initialData.ptime) {
      setStartDate(dayjs(initialData.ptime).toDate());
    }
  }, [initialData]);

  useEffect(() => {
    const { vki, obesityStatus } = calculateBMI(weight, height);
    setVki(vki);
    setObesityStatus(obesityStatus);
  }, [weight, height]);

  const resolveUpn = async (): Promise<number | null> => {
    // Hook zaten UPN'i fetch ediyor, sadece mevcut değeri döndür
    return upn;
  };

  const handleSave = async () => {
    const isEdit = mode === "edit";

    // Boy ve kilo zorunluluk kontrolü
    if (!height || height.trim() === "" || !weight || weight.trim() === "") {
      const missingFields = [];
      if (!height || height.trim() === "") {
        missingFields.push(t("height-weight.height-cm") || "Boy");
      }
      if (!weight || weight.trim() === "") {
        missingFields.push(t("height-weight.weight-kg") || "Kilo");
      }
      showMessage(
        missingFields.map(field => `${field} ${t("enter") || "giriniz"}`).join(", "),
        "error"
      );
      return;
    }

    if (!isEdit) {
      if (!patientIdNumber || patientIdNumber <= 0) {
        showMessage(t("error-loading-data") || "Hasta seçimi bulunamadı", "error");
        return;
      }
    }

    const resolvedUpn = await resolveUpn();
    if (!resolvedUpn && !isEdit) {
      showMessage(t("error-loading-data") || "UPN bulunamadı", "error");
      return;
    }

    try {
      setSaving(true);
      if (isEdit && recordId !== undefined && recordId !== null) {
        await referenceService.patient.UpdatePatientHeightWeightTrace(recordId, {
          height: height || null,
          weight: weight || null,
          vki: vki || null,
          description: description || "",
          write_on_epicrise: writeOnEpicrise ? "1" : "0",
          vki_mcpd_id: 0,
          ptime: toLocalISOWithMillis(dayjs(startDate).toDate()),
        });
        showMessage(t("save-success") || "Kayıt güncellendi", "success");
      } else {
        await referenceService.patient.AddPatientHeightWeightTrace({
          pt_id: patientIdNumber,
          upn: resolvedUpn as number,
          height: height || null,
          weight: weight || null,
          vki: vki || null,
          description: description || "",
          write_on_epicrise: writeOnEpicrise ? "1" : "0",
          vki_mcpd_id: 0,
          ptime: toLocalISOWithMillis(dayjs(startDate).toDate()),
        });
        showMessage(t("save-success") || "Kayıt eklendi", "success");
      }
      onSaved?.();
      onCloseModal?.();
    } catch (error) {
      console.error(
        isEdit
          ? "UpdatePatientHeightWeightTrace error:"
          : "AddPatientHeightWeightTrace error:",
        error
      );
      showMessage(t("save-error") || "Kaydetme hatası", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Backdrop
        open={saving}
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Box sx={{ padding: '20px', width: '100%' }}>
        <Box sx={{ mb: 2 }}>
          <ByzNurseSaveButton onClick={handleSave} loading={saving} disabled={saving} />
        </Box>
        <Grid container spacing={2}>
          <Grid md={6} xs={6} container direction={'column'} spacing={3}>
            <Grid md={6} xs={6} sx={{ backgroundColor: '#F4F7FE', borderBottom: '1px solid #E0E0E0' }}>
              <ByzMuiTypography sx={{ display: 'flex', textAlign: 'center' }} fontWeight={500} fontSize={'14px'}>
                {t("height-weight.title") || "Boy Kilo Takibi"}
              </ByzMuiTypography>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid md={6} xs={6} sx={{ borderBottom: '1px solid #E0E0E0', paddingRight: '10px', display: 'flex' }}>
                <ByzMuiTypography fontWeight={500} fontSize="14px" sx={{ marginLeft: '30px' }}>
                  {t("datetime") || "Tarih-Saat"}
                </ByzMuiTypography>
              </Grid>
              <Grid md={6} xs={6} sx={{ paddingTop: '0px' }}>
                <ByzMuiDateTimePicker
                  sx={{ width: 300, maxWidth: 300 }}
                  value={startDate}
                  onChange={(d: any) => setStartDate(d)}
                  disabled
                  slotProps={{
                    textField: {
                      inputProps: { readOnly: true },
                      sx: { width: 300, maxWidth: 300 },
                      placeholder: ""
                    }
                  }}
                />
              </Grid>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid md={6} xs={6} sx={{ borderBottom: '1px solid #E0E0E0', paddingRight: '10px' }}>
                <ByzMuiTypography fontWeight={500} fontSize="14px" sx={{ marginLeft: '30px' }}>
                  {t("height-weight.user") || "Kullanıcı"}
                </ByzMuiTypography>
              </Grid>
              <Grid md={6} xs={6} sx={{ paddingTop: '0px' }}>
                <ByzMuiTextField
                  sx={{ width: 300, maxWidth: 300 }}
                  color='info'
                  disabled
                  label={t("user", "capitalize") || "Kullanıcı"}
                  variant="outlined"
                  value={displayUser}
                  inputProps={{ readOnly: true }}
                />
              </Grid>
            </Grid>

            <Grid md={6} xs={6} sx={{ backgroundColor: '#F4F7FE', borderBottom: '1px solid #E0E0E0' }}>
              <ByzMuiTypography sx={{ display: 'flex', textAlign: 'center' }} fontWeight={500} fontSize={'14px'}>
                {t("height-weight.values") || "Değerler"}
              </ByzMuiTypography>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid md={6} xs={6} sx={{ borderBottom: '1px solid #E0E0E0', paddingRight: '10px' }}>
                <ByzMuiTypography fontWeight={500} fontSize="14px" sx={{ marginLeft: '30px' }}>
                  {t("height-weight.height-cm") || "Boy (cm)"}
                </ByzMuiTypography>
              </Grid>
              <Grid md={6} xs={6} sx={{ paddingTop: '0px' }}>
                <ByzMuiTextField
                  sx={{ width: 300, maxWidth: 300 }}
                  color='info'
                  label={t("enter") || "Giriniz"}
                  noPlaceholder
                  variant="outlined"
                  type="number"
                  inputProps={{ inputMode: "numeric", pattern: "\\d*", step: 1 }}
                  value={height}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (!/^[0-9]*$/.test(value)) return;
                    if (value.length > 3) return;
                    setHeight(value);
                  }}
                />
              </Grid>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid md={6} xs={6} sx={{ borderBottom: '1px solid #E0E0E0', paddingRight: '10px' }}>
                <ByzMuiTypography fontWeight={500} fontSize="14px" sx={{ marginLeft: '30px' }}>
                  {t("height-weight.weight-kg") || "Kilo (kg)"}
                </ByzMuiTypography>
              </Grid>
              <Grid md={6} xs={6} sx={{ paddingTop: '0px' }}>
                <ByzMuiTextField
                  sx={{ width: 300, maxWidth: 300 }}
                  color='info'
                  label={t("enter") || "Giriniz"}
                  noPlaceholder
                  variant="outlined"
                  type="number"
                  inputProps={{ inputMode: "numeric", pattern: "\\d*", step: 1 }}
                  value={weight}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (!/^[0-9.,]*$/.test(value)) return;
                    const parts = value.split(/[,\.]/);
                    if (parts.length > 2 || parts[0].length > 3 || (parts[1] && parts[1].length > 4)) return;
                    setWeight(value);
                  }}
                />
              </Grid>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid md={6} xs={6} sx={{ borderBottom: '1px solid #E0E0E0', paddingRight: '10px' }}>
                <ByzMuiTypography fontWeight={500} fontSize="14px" sx={{ marginLeft: '30px' }}>
                  {t("height-weight.bmi-index") || "VKI Vücut Kitle İndeksi"}
                </ByzMuiTypography>
              </Grid>
              <Grid md={6} xs={6} sx={{ paddingTop: '0px' }}>
                <ByzMuiTextField
                  sx={{ width: 300, maxWidth: 300 }}
                  color='info'
                  label={t("enter") || "Giriniz"}
                  noPlaceholder
                  variant="outlined"
                  value={vki}
                  disabled={true}
                />
              </Grid>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid md={6} xs={6} sx={{ borderBottom: '1px solid #E0E0E0', paddingRight: '10px' }}>
                <ByzMuiTypography fontWeight={500} fontSize="14px" sx={{ marginLeft: '30px' }}>
                  {t("height-weight.bmi-obesity-assessment") || "VKI Obezite Değerlendirmesi"}
                </ByzMuiTypography>
              </Grid>
              <Grid md={6} xs={6} sx={{ paddingTop: '0px' }}>
                <ByzMuiTextField
                  sx={{ width: 300, maxWidth: 300 }}
                  color='info'
                  label={t("enter") || "Giriniz"}
                  noPlaceholder
                  variant="outlined"
                  value={obesityStatus}
                  disabled={true}
                />
              </Grid>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid md={6} xs={6} sx={{ borderBottom: '1px solid #E0E0E0', paddingRight: '10px' }}>
                <ByzMuiTypography fontWeight={500} fontSize="14px" sx={{ marginLeft: '30px' }}>
                  {t("height-weight.description") || "Açıklama"}
                </ByzMuiTypography>
              </Grid>
              <Grid md={6} xs={6} sx={{ paddingTop: '0px' }}>
                <ByzMuiTextField
                  sx={{ width: 300, maxWidth: 300 }}
                  color='info'
                  label={t("enter") || "Giriniz"}
                  noPlaceholder
                  variant="outlined"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </Grid>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid md={6} xs={6} sx={{ borderBottom: '1px solid #E0E0E0', paddingRight: '10px' }}>
                <ByzMuiTypography fontWeight={500} fontSize="14px" sx={{ marginLeft: '30px' }}>
                  {t("height-weight.epicrisis-output") || "Epikrizde Çıksın"}
                </ByzMuiTypography>
              </Grid>
              <Grid md={6} xs={6} sx={{ paddingTop: '0px' }}>
                <ByzMuiCheckBox checked={writeOnEpicrise} onChange={(e) => setWriteOnEpicrise(e.target.checked)} />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>

      <ByzMuiSnackBar
        open={alertOpen}
        onClose={() => setAlertOpen(false)}
        title=""
        message={alertMessage}
        type={alertType}
        anchorOrigin={alertType === "error" ? { vertical: "top", horizontal: "right" } : { vertical: "bottom", horizontal: "right" }}
      />
    </>
  );
}

export default HeightWeightCreate;